// Standalone replay viewer page - lazy loaded from app.ts
import type { Replay, GameEvent, DebugInfo, Position, ViewMode } from '../types';
import { fetchCommentary, submitMapVote, fetchMapVotes } from '../api-types';
import {
  AnnotationOverlay,
  createAnnotationForm,
  fetchFeedback,
  loadLocalAnnotations,
  ANNOTATION_OVERLAY_STYLES,
  type Annotation,
} from '../components/annotation';
import {
  EventTimeline,
  EVENT_TIMELINE_STYLES,
} from '../components/event-timeline';
import {
  computeAllDensities,
  computeSpeedSchedule,
  createDirectorState,
  tickDirectorSpeed,
  loadDirectorConfig,
  saveDirectorConfig,
  formatDirectorLabel,
  type DirectorConfig,
  type DirectorState,
  type DurationPreset,
} from '../components/director';
import { THEATER_STYLES, TheaterMode } from '../components/theater';
import { setActiveReplay } from '../components/pip-registry';
import { getPipMatchId, restorePip } from '../components/pip';
import { fetchReplayFromUrl } from '../lib/replay-data';
import { hapticPulse, isHapticEnabled, setHapticEnabled } from '../lib/ambient';

const loadReplayViewer = () => import('../replay-viewer');

export function renderReplayPage(params: Record<string, string>): void {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = `
    <div class="replay-page">
      <h1 class="page-title">Replay Viewer</h1>
      <div id="replay-loading" style="text-align: center; padding: 60px 20px; color: var(--text-muted);">
        Loading replay viewer...
      </div>
    </div>
  `;

  loadReplayViewer().then(({ ReplayViewer }) => {
    // If params.url is not set but params.id is, construct the URL from the match ID
    const replayUrl = params.url || (params.id ? `/data/replays/${params.id}.json.gz` : undefined);
    initReplayViewerWithClass(ReplayViewer, replayUrl);
  });
}

function initReplayViewerWithClass(ReplayViewerClass: any, initialUrl?: string): void {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = `
    <div class="replay-page">
      <h1 class="page-title">Replay Viewer</h1>

      <div class="replay-layout">
        <div class="replay-main">
          <div class="canvas-wrapper" style="position:relative">
            <canvas id="replay-canvas" style="touch-action:none"></canvas>
            <div id="no-replay" class="no-replay-message">${initialUrl ? 'Loading replay…' : 'Enter a replay URL to load'}</div>
            <button id="theater-btn" class="theater-btn" aria-label="Toggle theater mode" title="Theater mode (F)" style="position:absolute;top:8px;right:8px">&#x26F6;</button>
            <div id="follow-indicator" class="follow-indicator" style="display:none;position:absolute;top:8px;left:8px;background:rgba(0,0,0,0.75);color:#e5e7eb;font:12px monospace;padding:4px 10px;border-radius:4px;pointer-events:auto;z-index:10;cursor:pointer" role="status" aria-live="polite"></div>
            <div id="minimap-container" style="position:absolute;bottom:16px;right:16px;border:2px solid var(--border,#475569);border-radius:6px;overflow:hidden;box-shadow:0 4px 12px rgba(0,0,0,0.5);opacity:0.85;transition:opacity 0.2s;z-index:10">
              <canvas id="minimap-canvas" width="150" height="150" style="display:block;cursor:crosshair"></canvas>
            </div>
          </div>

          <!-- Mobile compact controls bar — CSS hides on tablet+ -->
          <div class="mobile-replay-controls" id="mobile-controls">
            <div class="mobile-playback-bar">
              <button id="mobile-reset-btn" class="btn small" aria-label="Reset to start" disabled>&#9612;&#9612;</button>
              <button id="mobile-prev-btn" class="btn small" aria-label="Previous turn" disabled>&#9664;</button>
              <button id="mobile-play-btn" class="btn small primary" aria-label="Play or pause" disabled>&#9654;</button>
              <button id="mobile-next-btn" class="btn small" aria-label="Next turn" disabled>&#9654;&#9654;</button>
              <span id="mobile-turn-info" class="mobile-speed-display">T: 0/0</span>
              <button id="mobile-speed-btn" class="btn small secondary" aria-label="Cycle playback speed">100ms</button>
            </div>
            <input type="range" id="mobile-turn-slider" min="0" max="0" value="0"
              style="width:100%;margin-top:4px" disabled aria-label="Turn scrubber">
          </div>

          <!-- Mobile event timeline ribbon — CSS hides on tablet+ -->
          <div class="mobile-event-timeline" id="mobile-timeline" aria-label="Event timeline">
            <span style="color:var(--text-muted);font-size:0.75rem;padding:4px 8px">${initialUrl ? 'Loading…' : 'Enter a URL to load'}</span>
          </div>

          <!-- Desktop event timeline with annotation badges (hidden on mobile) -->
          <div class="event-timeline-container" id="event-timeline-container" style="display:none"></div>

          <div id="win-prob-section" class="win-prob-section" style="display:none">
            <div class="win-prob-header">
              <span class="win-prob-title">Win Probability</span>
              <div class="critical-moment-nav">
                <button id="prev-critical-btn" class="btn" title="Previous critical moment ([)" disabled>&#9664; Prev</button>
                <span id="critical-moment-info" class="critical-moment-info">&#8212;</span>
                <button id="next-critical-btn" class="btn" title="Next critical moment (])" disabled>Next &#9654;</button>
              </div>
            </div>
            <div id="win-prob-container" class="win-prob-container"></div>
            <div id="win-prob-legend" class="win-prob-legend"></div>
          </div>
        </div>

        <div id="commentary-bar" class="commentary-bar" style="display:none">
          <div class="commentary-content">
            <span id="commentary-text" class="commentary-text"></span>
          </div>
          <button id="commentary-toggle" class="btn small secondary" title="Toggle AI commentary">💬</button>
        </div>

        <div class="replay-sidebar">
          <div class="panel"${initialUrl ? ' style="display:none"' : ''}>
            <h2>Load Replay</h2>
            <div class="load-controls">
              <div class="url-input-group">
                <input type="text" id="url-input" placeholder="Enter replay URL...">
                <button id="load-url-btn" class="btn primary">Load</button>
              </div>
            </div>
          </div>
          <!-- Hidden inputs always present so JS references resolve -->
          <input type="file" id="file-input" accept=".json" style="display:none">

          <div class="panel">
            <h2>Playback</h2>
            <div class="playback-controls">
              <button id="play-btn" class="btn" disabled>Play</button>
              <button id="prev-btn" class="btn" disabled>Prev</button>
              <button id="next-btn" class="btn" disabled>Next</button>
              <button id="reset-btn" class="btn" disabled>Reset</button>
            </div>
            <div class="slider-group">
              <label>Turn: <span id="turn-display">0</span> / <span id="total-turns">0</span></label>
              <input type="range" id="turn-slider" min="0" max="0" value="0" disabled>
            </div>
            <div class="slider-group">
              <label>Speed: <span id="speed-display">100</span>ms/turn</label>
              <input type="range" id="speed-slider" min="20" max="1000" value="100">
            </div>
            <div class="speed-selector-group">
              <label for="speed-select">Speed Preset:</label>
              <select id="speed-select">
                <option value="500">1x</option>
                <option value="250">2x</option>
                <option value="125">4x</option>
                <option value="62">8x</option>
                <option value="31">16x</option>
                <option value="director">Director</option>
              </select>
            </div>
            <div id="director-options" class="director-options" style="display:none">
              <div class="slider-group">
                <label>Target Duration:</label>
                <div class="duration-presets" id="duration-presets">
                  <button class="btn small secondary duration-btn" data-duration="30">30s</button>
                  <button class="btn small secondary duration-btn active" data-duration="60">1min</button>
                  <button class="btn small secondary duration-btn" data-duration="120">2min</button>
                  <button class="btn small secondary duration-btn" data-duration="300">5min</button>
                </div>
              </div>
              <div class="director-status" id="director-status">Director 16x</div>
            </div>
          </div>

          <div class="panel">
            <h2>View Options</h2>
            <div class="view-options">
              <label for="view-mode-select">View Mode:</label>
              <select id="view-mode-select">
                <option value="standard">Standard</option>
                <option value="dots">Dots</option>
                <option value="voronoi">Territory (Voronoi)</option>
                <option value="influence">Influence Gradient</option>
              </select>
              <label for="fog-select" style="margin-top: 10px;">Fog of War:</label>
              <select id="fog-select">
                <option value="">Omniscient</option>
              </select>
              <label for="cell-size-select" style="margin-top: 10px;">Cell Size:</label>
              <select id="cell-size-select">
                <option value="6">Small (6px)</option>
                <option value="8">Medium (8px)</option>
                <option value="10" selected>Large (10px)</option>
                <option value="12">X-Large (12px)</option>
              </select>
              <label for="follow-zoom-select" style="margin-top: 10px;">Follow Zoom:</label>
              <select id="follow-zoom-select">
                <option value="2">2x</option>
                <option value="3" selected>3x</option>
                <option value="4">4x</option>
                <option value="5">5x</option>
                <option value="6">6x</option>
              </select>
            </div>
          </div>

          <div class="panel">
            <h2>Accessibility</h2>
            <div class="accessibility-options">
              <label class="checkbox-label">
                <input type="checkbox" id="color-blind-toggle" checked>
                Color-blind safe palette
              </label>
              <label class="checkbox-label">
                <input type="checkbox" id="shapes-toggle" checked>
                Shapes per player
              </label>
              <label class="checkbox-label">
                <input type="checkbox" id="high-contrast-toggle">
                High contrast mode
              </label>
              <label class="checkbox-label">
                <input type="checkbox" id="reduced-motion-toggle">
                Reduced motion
              </label>
              <label class="checkbox-label">
                <input type="checkbox" id="haptic-toggle" checked>
                Haptic feedback (mobile)
              </label>
            </div>
          </div>

          <div class="panel">
            <h2>Match Info</h2>
            <dl class="match-info">
              <dt>Match ID</dt>
              <dd id="info-match-id">-</dd>
              <dt>Winner</dt>
              <dd id="info-winner">-</dd>
              <dt>Turns</dt>
              <dd id="info-turns">-</dd>
              <dt>Reason</dt>
              <dd id="info-reason">-</dd>
            </dl>
          </div>

          <div class="panel" id="map-vote-panel" style="display:none">
            <h2>Map</h2>
            <dl class="match-info">
              <dt>Dimensions</dt>
              <dd id="map-info-dimensions">-</dd>
              <dt>Wall Density</dt>
              <dd id="map-info-wall-density">-</dd>
              <dt>Energy Nodes</dt>
              <dd id="map-info-energy-count">-</dd>
            </dl>
            <div class="map-vote-section" id="map-vote-section">
              <span class="map-vote-label">Rate this map</span>
              <div class="map-vote-controls">
                <button class="map-vote-btn" id="map-vote-up" title="Upvote this map" disabled>&#128077;</button>
                <span class="map-vote-count" id="map-vote-count">0</span>
                <button class="map-vote-btn" id="map-vote-down" title="Downvote this map" disabled>&#128078;</button>
              </div>
              <div class="map-vote-status" id="map-vote-status"></div>
            </div>
          </div>

          <!-- Transcript panel (§15.3) -->
          <div class="panel transcript-panel" id="transcript-panel">
            <div class="transcript-panel-header" id="transcript-panel-toggle-btn" role="button" tabindex="0"
                 aria-expanded="false" aria-controls="transcript-panel-body">
              <h2 style="margin:0">Transcript</h2>
              <span id="transcript-panel-chevron" class="transcript-chevron" aria-hidden="true">▾</span>
            </div>
            <div id="transcript-panel-body" class="transcript-panel-body">
              <div id="transcript-content" class="transcript-content">
                <div class="no-transcript">Load a replay to view transcript</div>
              </div>
            </div>
          </div>

          <div class="panel">
            <h2>Events This Turn</h2>
            <div class="event-log" id="event-log">
              <div class="no-events">No events</div>
            </div>
          </div>

          <div class="panel annotation-panel" id="annotation-panel">
            <h2>Annotations</h2>
            <div id="annotation-overlay-container"></div>
            <div id="annotation-form-container"></div>
          </div>

          <div class="keyboard-shortcuts">
            <kbd>Space</kbd> Play/Pause
            <kbd>←</kbd><kbd>→</kbd> Step
            <kbd>Shift</kbd>+<kbd>←</kbd><kbd>→</kbd> Jump 10
            <kbd>[</kbd><kbd>]</kbd> Prev/Next Critical
            <kbd>Home</kbd><kbd>End</kbd> First/Last
            <kbd>1</kbd>-<kbd>6</kbd> Follow Bot
            <kbd>0</kbd>/<kbd>Esc</kbd> Exit Follow
            <kbd>V</kbd> View Mode
            <kbd>F</kbd> Theater
            <kbd>E</kbd> Events
            <kbd>C</kbd> Commentary
            <kbd>T</kbd> Transcript
            <kbd>?</kbd> Shortcuts
          </div>
        </div>
      </div>
    </div>

    <!-- Floating view mode toggle — CSS hides on tablet+desktop -->
    <button id="mobile-view-mode-btn" class="mobile-view-mode-toggle"
      aria-label="Switch view mode" title="Switch view mode">&#128065;</button>

    <!-- Debug telemetry bottom sheet (mobile) / sidebar panel (desktop) -->
    <div id="debug-panel" class="panel debug-panel" style="display:none">
      <div class="debug-panel-header" id="debug-panel-toggle-btn" role="button" tabindex="0"
           aria-expanded="false" aria-controls="debug-panel-body">
        <h2 style="margin:0">Debug Telemetry</h2>
        <span id="debug-panel-chevron" class="debug-chevron" aria-hidden="true">▾</span>
      </div>
      <div id="debug-panel-body" class="debug-panel-body">
        <div id="debug-player-toggles" class="debug-player-toggles"></div>
        <div id="debug-info-display" class="debug-info-display">
          <div class="no-debug-data">No debug data for this turn</div>
        </div>
      </div>
    </div>

    <style>
      .replay-page .page-title { margin-bottom: 20px; }
      .replay-layout { display: flex; gap: 20px; }
      .replay-main { flex: 1; min-width: 0; }
      .canvas-wrapper { background-color: var(--bg-secondary); border-radius: 8px; padding: 10px; overflow: auto; max-height: 80vh; }
      #replay-canvas { display: block; }
      .no-replay-message { color: var(--text-muted); text-align: center; padding: 60px 20px; }
      .replay-sidebar { width: 300px; flex-shrink: 0; display: flex; flex-direction: column; gap: 15px; }
      .panel { background-color: var(--bg-secondary); border-radius: 8px; padding: 15px; }
      .panel h2 { font-size: 1rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 12px; }
      .load-controls { display: flex; flex-direction: column; gap: 10px; }
      .url-input-group { display: flex; gap: 8px; }
      .url-input-group input { flex: 1; background-color: var(--bg-primary); border: 1px solid var(--border); color: var(--text-primary); padding: 8px; border-radius: 6px; font-size: 14px; }
      .playback-controls { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
      .playback-controls .btn:disabled { opacity: 0.5; cursor: not-allowed; }
      .slider-group { margin-bottom: 10px; }
      .slider-group label { display: block; color: var(--text-muted); font-size: 0.875rem; margin-bottom: 6px; }
      .slider-group input[type="range"] { width: 100%; }
      .view-options { display: flex; flex-direction: column; }
      .view-options label { color: var(--text-muted); font-size: 0.875rem; margin-bottom: 6px; }
      .view-options select { background-color: var(--bg-primary); border: 1px solid var(--border); color: var(--text-primary); padding: 8px; border-radius: 6px; font-size: 14px; }
      .accessibility-options { display: flex; flex-direction: column; gap: 8px; }
      .checkbox-label { display: flex; align-items: center; gap: 8px; cursor: pointer; color: var(--text-muted); font-size: 0.875rem; }
      .checkbox-label input[type="checkbox"] { width: 16px; height: 16px; accent-color: var(--accent); cursor: pointer; }
      .checkbox-label:hover { color: var(--text-primary); }
      .match-info dt { color: var(--text-muted); font-size: 0.75rem; text-transform: uppercase; margin-top: 10px; }
      .match-info dd { color: var(--text-primary); }
      .event-log { max-height: 150px; overflow-y: auto; font-size: 0.75rem; font-family: monospace; }
      .event-log .event { padding: 4px 0; border-bottom: 1px solid var(--bg-tertiary); }
      .event-log .event:last-child { border-bottom: none; }
      .no-events { color: var(--text-muted); }
      .keyboard-shortcuts { font-size: 0.75rem; color: var(--text-muted); }
      .keyboard-shortcuts kbd { background-color: var(--bg-tertiary); padding: 2px 6px; border-radius: 4px; font-family: monospace; margin-right: 4px; }
      .speed-selector-group { margin-bottom: 10px; }
      .speed-selector-group label { display: block; color: var(--text-muted); font-size: 0.875rem; margin-bottom: 6px; }
      .speed-selector-group select { width: 100%; background-color: var(--bg-primary); border: 1px solid var(--border); color: var(--text-primary); padding: 8px; border-radius: 6px; font-size: 14px; }
      .director-options { margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--bg-tertiary); }
      .duration-presets { display: flex; gap: 6px; flex-wrap: wrap; }
      .duration-presets .btn { flex: 1; min-width: 40px; text-align: center; font-size: 0.75rem; }
      .duration-presets .btn.active { background-color: var(--accent); color: white; }
      .director-status { text-align: center; color: var(--accent); font-size: 0.8rem; font-weight: 600; padding: 6px 0; font-family: monospace; }
      .win-prob-section { background-color: var(--bg-secondary); border-radius: 8px; padding: 12px; margin-top: 10px; }
      .win-prob-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; flex-wrap: wrap; gap: 8px; }
      .win-prob-title { color: var(--text-muted); font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
      .critical-moment-nav { display: flex; align-items: center; gap: 8px; }
      .critical-moment-nav .btn { padding: 4px 10px; font-size: 0.75rem; }
      .critical-moment-nav .btn:disabled { opacity: 0.4; cursor: not-allowed; }
      .critical-moment-info { color: var(--text-muted); font-size: 0.8rem; max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .win-prob-container { width: 100%; overflow: hidden; border-radius: 4px; }
      .win-prob-legend { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 6px; font-size: 0.75rem; font-family: monospace; }
      .commentary-bar { background-color: var(--bg-secondary); border-radius: 8px; padding: 8px 12px; margin-top: 10px; display: flex; align-items: center; gap: 10px; min-height: 40px; }
      .commentary-content { flex: 1; min-width: 0; }
      .commentary-text { color: var(--text-secondary); font-size: 0.875rem; line-height: 1.4; display: block; }
      .commentary-text.type-setup { color: #94a3b8; }
      .commentary-text.type-action { color: #e2e8f0; }
      .commentary-text.type-reaction { color: #fbbf24; }
      .commentary-text.type-climax { color: #f97316; font-weight: 600; }
      .commentary-text.type-denouement { color: #94a3b8; font-style: italic; }
      .commentary-toggle { flex-shrink: 0; opacity: 0.6; transition: opacity 0.2s; }
      .commentary-toggle.active { opacity: 1; background-color: var(--accent); color: white; }
      /* Map voting widget */
      .map-vote-section { margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--bg-tertiary); }
      .map-vote-label { display: block; color: var(--text-muted); font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
      .map-vote-controls { display: flex; align-items: center; gap: 12px; justify-content: center; }
      .map-vote-btn { background: var(--bg-tertiary); border: 1px solid var(--border); color: var(--text-primary); font-size: 1.25rem; width: 44px; height: 44px; border-radius: 8px; cursor: pointer; transition: all 0.15s ease; display: flex; align-items: center; justify-content: center; }
      .map-vote-btn:hover:not(:disabled) { background: var(--accent); color: white; border-color: var(--accent); }
      .map-vote-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      .map-vote-btn.voted-up { background: #22c55e; color: white; border-color: #22c55e; opacity: 1; }
      .map-vote-btn.voted-down { background: #ef4444; color: white; border-color: #ef4444; opacity: 1; }
      .map-vote-count { font-size: 1.1rem; font-weight: 600; min-width: 32px; text-align: center; color: var(--text-primary); font-family: monospace; }
      .map-vote-count.positive { color: #22c55e; }
      .map-vote-count.negative { color: #ef4444; }
      .map-vote-status { text-align: center; color: var(--text-muted); font-size: 0.7rem; margin-top: 6px; min-height: 1em; }
      @media (max-width: 900px) {
        .replay-layout { flex-direction: column; }
        .replay-sidebar { width: 100%; }
      }
      /* Debug telemetry panel */
      .debug-panel { padding: 0; overflow: hidden; }
      .debug-panel-header { display: flex; align-items: center; justify-content: space-between; padding: 15px; cursor: pointer; user-select: none; }
      .debug-panel-header:hover { background-color: var(--bg-tertiary); }
      .debug-panel-header h2 { font-size: 1rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; }
      .debug-chevron { color: var(--text-muted); font-size: 1rem; transition: transform 0.2s; }
      .debug-panel.expanded .debug-chevron { transform: rotate(180deg); }
      .debug-panel-body { display: none; padding: 0 15px 15px; }
      .debug-panel.expanded .debug-panel-body { display: block; }
      .debug-player-toggles { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
      .debug-player-toggle { display: flex; align-items: center; gap: 8px; cursor: pointer; color: var(--text-muted); font-size: 0.875rem; }
      .debug-player-toggle input[type="checkbox"] { width: 16px; height: 16px; accent-color: var(--accent); cursor: pointer; }
      .debug-player-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
      .debug-info-display { display: flex; flex-direction: column; gap: 10px; }
      .debug-player-info { background-color: var(--bg-tertiary); border-radius: 6px; padding: 10px; }
      .debug-player-name { font-size: 0.75rem; color: var(--text-muted); text-transform: uppercase; margin-bottom: 6px; font-weight: 600; }
      .debug-reasoning { color: var(--text-secondary); font-size: 0.8rem; line-height: 1.5; margin-bottom: 8px; }
      .debug-values { display: flex; flex-direction: column; gap: 4px; margin-bottom: 8px; }
      .debug-value-item { font-size: 0.75rem; font-family: monospace; color: var(--text-muted); display: flex; align-items: center; gap: 6px; }
      .debug-value-key { font-weight: 600; }
      .debug-value-value { color: var(--text-secondary); }
      .debug-targets { display: flex; flex-direction: column; gap: 4px; }
      .debug-target-item { font-size: 0.75rem; font-family: monospace; color: var(--text-muted); display: flex; align-items: center; gap: 6px; }
      .debug-target-priority { opacity: 0.7; }
      .debug-heatmap { font-size: 0.75rem; color: var(--text-muted); display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
      .debug-heatmap-label { font-weight: 600; }
      .debug-heatmap-name { color: var(--accent); }
      .debug-heatmap-size { opacity: 0.7; }
      .no-debug-data { color: var(--text-muted); font-size: 0.8rem; font-style: italic; }
      /* Transcript panel (§15.3) */
      .transcript-panel { padding: 0; overflow: hidden; }
      .transcript-panel-header { display: flex; align-items: center; justify-content: space-between; padding: 15px; cursor: pointer; user-select: none; }
      .transcript-panel-header:hover { background-color: var(--bg-tertiary); }
      .transcript-panel-header h2 { font-size: 1rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; }
      .transcript-chevron { color: var(--text-muted); font-size: 1rem; transition: transform 0.2s; }
      .transcript-panel.expanded .transcript-chevron { transform: rotate(180deg); }
      .transcript-panel-body { display: none; padding: 0 15px 15px; }
      .transcript-panel.expanded .transcript-panel-body { display: block; }
      .transcript-content { max-height: 400px; overflow-y: auto; font-size: 0.8rem; line-height: 1.6; }
      .transcript-entry { padding: 8px 0; border-bottom: 1px solid var(--bg-tertiary); }
      .transcript-entry:last-child { border-bottom: none; }
      .transcript-entry.turn-current { background-color: var(--accent); color: white; padding: 8px; border-radius: 4px; margin: 4px 0; }
      .transcript-turn-number { font-weight: 600; color: var(--text-muted); margin-right: 8px; }
      .transcript-entry.turn-current .transcript-turn-number { color: rgba(255,255,255,0.7); }
      .no-transcript { color: var(--text-muted); font-size: 0.8rem; font-style: italic; }
      /* Mobile: bottom sheet for transcript */
      @media (max-width: 900px) {
        .transcript-panel {
          position: fixed !important;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 200;
          border-radius: 12px 12px 0 0 !important;
          max-height: 70vh;
          overflow-y: auto;
          transform: translateY(calc(100% - 52px));
          transition: transform 0.3s ease;
          box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.4);
        }
        .transcript-panel.expanded {
          transform: translateY(0);
        }
        .transcript-panel-header::before {
          content: '';
          display: block;
          width: 36px;
          height: 4px;
          background: var(--border, #374151);
          border-radius: 2px;
          position: absolute;
          top: 8px;
          left: 50%;
          transform: translateX(-50%);
        }
        .transcript-panel-header { position: relative; padding-top: 20px; }
      }
      /* Mobile: bottom sheet */
      @media (max-width: 900px) {
        .debug-panel {
          position: fixed !important;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 200;
          border-radius: 12px 12px 0 0 !important;
          max-height: 70vh;
          overflow-y: auto;
          transform: translateY(calc(100% - 52px));
          transition: transform 0.3s ease;
          box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.4);
        }
        .debug-panel.expanded {
          transform: translateY(0);
        }
        .debug-panel-header::before {
          content: '';
          display: block;
          width: 36px;
          height: 4px;
          background: var(--border, #374151);
          border-radius: 2px;
          position: absolute;
          top: 8px;
          left: 50%;
          transform: translateX(-50%);
        }
        .debug-panel-header { position: relative; padding-top: 20px; }
      }
      /* Annotation panel */
      .annotation-panel { padding: 0; overflow: hidden; }
      .annotation-panel > h2 { padding: 15px 15px 0; }
      #annotation-overlay-container { padding: 0 15px; }
      #annotation-form-container { padding: 0 15px 15px; border-top: 1px solid var(--bg-tertiary); margin-top: 8px; padding-top: 10px; }
      .annotation-canvas-hint { position: absolute; bottom: 8px; left: 8px; background: rgba(0,0,0,0.7); color: #94a3b8; font-size: 0.65rem; padding: 3px 8px; border-radius: 4px; pointer-events: none; opacity: 0; transition: opacity 0.3s; }
      .annotation-canvas-hint.visible { opacity: 1; }
    </style>
    <style>${ANNOTATION_OVERLAY_STYLES}</style>
    <style>${EVENT_TIMELINE_STYLES}</style>
    <style>${THEATER_STYLES}</style>
  `;

  initReplayViewer(ReplayViewerClass, initialUrl);
}

function initReplayViewer(ReplayViewerClass: any, initialUrl?: string): void {
  const canvas = document.getElementById('replay-canvas') as HTMLCanvasElement;
  const noReplayDiv = document.getElementById('no-replay') as HTMLDivElement;
  const urlInput = document.getElementById('url-input') as HTMLInputElement;
  const loadUrlBtn = document.getElementById('load-url-btn') as HTMLButtonElement;
  const playBtn = document.getElementById('play-btn') as HTMLButtonElement;
  const prevBtn = document.getElementById('prev-btn') as HTMLButtonElement;
  const nextBtn = document.getElementById('next-btn') as HTMLButtonElement;
  const resetBtn = document.getElementById('reset-btn') as HTMLButtonElement;
  const turnDisplay = document.getElementById('turn-display') as HTMLSpanElement;
  const totalTurnsSpan = document.getElementById('total-turns') as HTMLSpanElement;
  const turnSlider = document.getElementById('turn-slider') as HTMLInputElement;
  const speedDisplay = document.getElementById('speed-display') as HTMLSpanElement;
  const speedSlider = document.getElementById('speed-slider') as HTMLInputElement;
  const fogSelect = document.getElementById('fog-select') as HTMLSelectElement;
  const cellSizeSelect = document.getElementById('cell-size-select') as HTMLSelectElement;
  const eventLogDiv = document.getElementById('event-log') as HTMLDivElement;
  const infoMatchId = document.getElementById('info-match-id') as HTMLElement;
  const infoWinner = document.getElementById('info-winner') as HTMLElement;
  const infoTurns = document.getElementById('info-turns') as HTMLElement;
  const infoReason = document.getElementById('info-reason') as HTMLElement;
  const winProbSection = document.getElementById('win-prob-section') as HTMLDivElement;
  const winProbContainer = document.getElementById('win-prob-container') as HTMLDivElement;
  const winProbLegend = document.getElementById('win-prob-legend') as HTMLDivElement;
  const prevCriticalBtn = document.getElementById('prev-critical-btn') as HTMLButtonElement;
  const nextCriticalBtn = document.getElementById('next-critical-btn') as HTMLButtonElement;
  const criticalMomentInfo = document.getElementById('critical-moment-info') as HTMLSpanElement;
  const commentaryBar = document.getElementById('commentary-bar') as HTMLDivElement;
  const commentaryText = document.getElementById('commentary-text') as HTMLSpanElement;
  const commentaryToggle = document.getElementById('commentary-toggle') as HTMLButtonElement;
  const debugPanel = document.getElementById('debug-panel') as HTMLDivElement;
  const debugPanelToggleBtn = document.getElementById('debug-panel-toggle-btn') as HTMLDivElement;
  const debugPlayerToggles = document.getElementById('debug-player-toggles') as HTMLDivElement;
  const debugInfoDisplay = document.getElementById('debug-info-display') as HTMLDivElement;
  const transcriptPanel = document.getElementById('transcript-panel') as HTMLDivElement;
  const transcriptPanelToggleBtn = document.getElementById('transcript-panel-toggle-btn') as HTMLDivElement;
  const transcriptContent = document.getElementById('transcript-content') as HTMLDivElement;

  // Mobile controls
  const mobilePlayBtn = document.getElementById('mobile-play-btn') as HTMLButtonElement;
  const mobilePrevBtn = document.getElementById('mobile-prev-btn') as HTMLButtonElement;
  const mobileNextBtn = document.getElementById('mobile-next-btn') as HTMLButtonElement;
  const mobileResetBtn = document.getElementById('mobile-reset-btn') as HTMLButtonElement;
  const mobileTurnInfo = document.getElementById('mobile-turn-info') as HTMLSpanElement;
  const mobileTurnSlider = document.getElementById('mobile-turn-slider') as HTMLInputElement;
  const mobileSpeedBtn = document.getElementById('mobile-speed-btn') as HTMLButtonElement;
  const mobileTimeline = document.getElementById('mobile-timeline') as HTMLDivElement;
  const viewModeSelect = document.getElementById('view-mode-select') as HTMLSelectElement;
  const mobileViewModeBtn = document.getElementById('mobile-view-mode-btn') as HTMLButtonElement;

  // §16.13: If PIP is active, restore the canvas from the mini-player.
  // The canvas-wrapper in the DOM is the inline target; we move the PIP canvas
  // back into it and reuse the viewer instance that's still running.
  const pipMatch = getPipMatchId();
  const canvasWrapper = document.querySelector('.canvas-wrapper') as HTMLElement;
  let viewer: any;
  let actualCanvas = canvas; // May be replaced by restored PIP canvas

  if (pipMatch && canvasWrapper && (initialUrl?.includes(pipMatch) || !initialUrl)) {
    const restored = restorePip();
    if (restored) {
      // Remove the placeholder canvas from DOM (PIP canvas has the real content)
      canvas.remove();
      // Move the PIP canvas back into the wrapper
      canvasWrapper.insertBefore(restored.canvas, canvasWrapper.firstChild);
      restored.canvas.style.display = 'block';
      restored.canvas.id = 'replay-canvas'; // Ensure ID is set for TheaterMode and other consumers
      actualCanvas = restored.canvas;
      viewer = new ReplayViewerClass(actualCanvas, { cellSize: 10 });
    } else {
      viewer = new ReplayViewerClass(actualCanvas, { cellSize: 10 });
    }
  } else {
    viewer = new ReplayViewerClass(actualCanvas, { cellSize: 10 });
  }

  // Wire up minimap canvas (§7.3)
  const minimapCanvas = document.getElementById('minimap-canvas') as HTMLCanvasElement;
  if (minimapCanvas) {
    viewer.setMinimapCanvas(minimapCanvas);
  }

  let criticalMoments: Array<{turn: number; delta: number; description: string}> = [];
  let commentaryEnabled = true;
  let debugPanelExpanded = false;
  let transcriptPanelExpanded = false;

  // Theater mode
  const theaterBtn = document.getElementById('theater-btn') as HTMLButtonElement;
  const theater = new TheaterMode(actualCanvas, {
    getScoreText: () => {
      const replay = viewer.getReplay() as Replay | null;
      if (!replay) return '';
      return replay.players.map((p: any, i: number) => `${p.name}: ${replay.result.scores?.[i] ?? 0}`).join('  ');
    },
    getPlayerColors: () => {
      const replay = viewer.getReplay() as Replay | null;
      if (!replay) return [];
      const palettes = ['#332288', '#88ccee', '#44aa99', '#117733', '#999933', '#ddcc77'];
      return replay.players.map((_: any, i: number) => palettes[i] ?? '#888888');
    },
    getWinProb: () => {
      const replay = viewer.getReplay() as Replay | null;
      if (!replay?.win_prob) return [];
      const turn = viewer.getTurn();
      return replay.win_prob[turn] ?? [];
    },
    getTurn: () => viewer.getTurn(),
    getTotalTurns: () => viewer.getTotalTurns(),
    getIsPlaying: () => viewer.getIsPlaying(),
    getSpeed: () => {
      const el = document.getElementById('speed-slider') as HTMLInputElement;
      return el ? parseInt(el.value, 10) : 100;
    },
    togglePlay: () => viewer.togglePlay(),
    setTurn: (t: number) => { viewer.setTurn(t); updateUI(); updateEventLog(); },
    exitTheater: () => {},
    onCriticalMoment: () => theater.pulseVignette(),
  });
  theaterBtn.addEventListener('click', () => theater.toggle());

  // Director mode state
  let directorState: DirectorState = createDirectorState();
  let directorConfig: DirectorConfig = loadDirectorConfig();
  let directorSchedule: ReturnType<typeof computeSpeedSchedule> = [];
  let directorAnimFrame: number | null = null;

  // Director UI elements
  const speedSelect = document.getElementById('speed-select') as HTMLSelectElement;
  const directorOptions = document.getElementById('director-options') as HTMLDivElement;
  const directorStatus = document.getElementById('director-status') as HTMLDivElement;
  const durationPresets = document.getElementById('duration-presets') as HTMLDivElement;

  // Mobile speed cycling
  const SPEED_STEPS = [1000, 500, 200, 100, 50, 20];
  let mobileSpeedIdx = 3; // default 100ms

  // View mode cycling
  const VIEW_MODES: Array<'standard' | 'dots' | 'voronoi' | 'influence'> = ['standard', 'dots', 'voronoi', 'influence'];
  const VIEW_MODE_ICONS: Record<string, string> = { standard: '\u{1F5FA}', dots: '··', voronoi: '⬡', influence: '◎' };

  // Pinch-to-zoom pointer state
  const activePointers = new Map<number, PointerEvent>();
  let pinchStartDist = 0;
  let pinchStartCellSize = 10;

  function enableControls(): void {
    playBtn.disabled = false;
    prevBtn.disabled = false;
    nextBtn.disabled = false;
    resetBtn.disabled = false;
    turnSlider.disabled = false;
    noReplayDiv.style.display = 'none';
  }

  function enableMobileControls(): void {
    mobilePlayBtn.disabled = false;
    mobilePrevBtn.disabled = false;
    mobileNextBtn.disabled = false;
    mobileResetBtn.disabled = false;
    mobileTurnSlider.disabled = false;
  }

  function updateMobileUI(): void {
    const turn = viewer.getTurn();
    const total = viewer.getTotalTurns();
    mobileTurnInfo.textContent = `T: ${turn}/${total - 1}`;
    mobileTurnSlider.value = String(turn);
    mobilePlayBtn.textContent = viewer.getIsPlaying() ? '⏸' : '▶';
  }

  function buildMobileTimeline(replay: Replay): void {
    const eventTurns: number[] = [];
    replay.turns.forEach((t: any, i: number) => {
      if (t.events && t.events.length > 0) eventTurns.push(i);
    });

    if (eventTurns.length === 0) {
      mobileTimeline.innerHTML = '<span style="color:var(--text-muted);font-size:0.75rem;padding:4px 8px">No events</span>';
      return;
    }

    const currentTurn = viewer.getTurn();
    mobileTimeline.innerHTML = eventTurns.map(turn => {
      const active = turn === currentTurn ? ' active' : '';
      return `<button class="mobile-event-dot${active}" data-turn="${turn}" aria-label="Turn ${turn}"><span style="font-size:0.65rem">${turn}</span></button>`;
    }).join('');

    mobileTimeline.querySelectorAll<HTMLElement>('.mobile-event-dot').forEach(dot => {
      dot.addEventListener('click', () => {
        const t = parseInt(dot.dataset.turn!, 10);
        viewer.setTurn(t);
        updateUI();
        updateEventLog();
        updateMobileUI();
        updateMobileTimeline();
      });
    });
  }

  function updateMobileTimeline(): void {
    const currentTurn = viewer.getTurn();
    mobileTimeline.querySelectorAll<HTMLElement>('.mobile-event-dot').forEach(dot => {
      const t = parseInt(dot.dataset.turn!, 10);
      dot.classList.toggle('active', t === currentTurn);
    });
    const activeDot = mobileTimeline.querySelector<HTMLElement>('.mobile-event-dot.active');
    if (activeDot) {
      activeDot.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }

  function updateUI(): void {
    turnDisplay.textContent = String(viewer.getTurn());
    totalTurnsSpan.textContent = String(viewer.getTotalTurns());
    turnSlider.value = String(viewer.getTurn());
    playBtn.textContent = 'Pause';
    if (!viewer.getReplay() || viewer.isAtEnd()) {
      playBtn.textContent = 'Play';
    }
  }

  function updateEventLog(): void {
    const events = viewer.getTurnEvents();
    if (events.length === 0) {
      eventLogDiv.innerHTML = '<div class="no-events">No events</div>';
      return;
    }
    eventLogDiv.innerHTML = events.map((e: GameEvent) => {
      const type = e.type.replace(/_/g, ' ');
      return `<div class="event"><span style="color: #fbbf24;">${type}</span></div>`;
    }).join('');
  }

  function updateMatchInfo(replay: Replay): void {
    infoMatchId.textContent = replay.match_id;
    infoTurns.textContent = String(replay.result.turns);
    infoReason.textContent = replay.result.reason;

    if (replay.result.winner >= 0 && replay.result.winner < replay.players.length) {
      infoWinner.textContent = replay.players[replay.result.winner].name;
    } else if (replay.result.winner === -1) {
      infoWinner.textContent = 'Draw';
    } else {
      infoWinner.textContent = 'Player ' + replay.result.winner;
    }

    fogSelect.innerHTML = '<option value="">Omniscient</option>';
    replay.players.forEach((player, idx) => {
      const option = document.createElement('option');
      option.value = String(idx);
      option.textContent = player.name;
      fogSelect.appendChild(option);
    });
  }

  function loadReplay(replay: Replay): void {
    viewer.loadReplay(replay);
    enableControls();
    enableMobileControls();
    updateMatchInfo(replay);
    turnSlider.max = String(viewer.getTotalTurns() - 1);
    mobileTurnSlider.max = String(viewer.getTotalTurns() - 1);
    updateUI();
    updateEventLog();
    updateMobileUI();
    buildMobileTimeline(replay);
    initWinProb(replay);
    initDirector(replay);
    loadCommentary(replay.match_id);
    initDebugPanel(replay);
    initAnnotations(replay);
    initMapVote(replay);
    updateTranscript();

    // §16.13: Register active replay for PIP support
    const pipCanvasWrapper = document.querySelector('.canvas-wrapper') as HTMLElement;
    const pipCanvasEl = (document.getElementById('replay-canvas') as HTMLCanvasElement)
      ?? document.querySelector('.canvas-wrapper canvas') as HTMLCanvasElement;
    if (pipCanvasEl && pipCanvasWrapper) {
      setActiveReplay({
        matchId: replay.match_id,
        canvas: pipCanvasEl,
        canvasWrapper: pipCanvasWrapper,
        getScoreText: () => {
          const r = viewer.getReplay() as Replay | null;
          if (!r) return '';
          return r.players.map((p: any, i: number) => `${p.name}: ${r.result.scores?.[i] ?? 0}`).join(' ');
        },
        getTurn: () => viewer.getTurn(),
        getTotalTurns: () => viewer.getTotalTurns(),
        getIsPlaying: () => viewer.getIsPlaying(),
        togglePlay: () => viewer.togglePlay(),
        pause: () => viewer.pause(),
      });
    }

    const hasAnyDebug = replay.turns.some(t => t.debug && Object.keys(t.debug).length > 0);
    if (hasAnyDebug) {
      debugPanel.style.display = '';
      // On mobile, default collapsed; on desktop, default expanded
      if (window.innerWidth > 900) {
        debugPanelExpanded = true;
        debugPanel.classList.add('expanded');
        debugPanelToggleBtn.setAttribute('aria-expanded', 'true');
      } else {
        debugPanelExpanded = false;
        debugPanel.classList.remove('expanded');
        debugPanelToggleBtn.setAttribute('aria-expanded', 'false');
      }
    } else {
      debugPanel.style.display = 'none';
    }
  }

  async function loadCommentary(matchId: string): Promise<void> {
    const commentary = await fetchCommentary(matchId);
    if (commentary && commentary.entries.length > 0) {
      viewer.setCommentary(commentary);
      commentaryBar.style.display = 'flex';
      commentaryToggle.classList.add('active');
      commentaryEnabled = true;
    } else {
      viewer.setCommentary(null);
      commentaryBar.style.display = 'none';
    }
  }

  // ── Map voting widget (§14.6) ──────────────────────────────────────────────────

  const mapVotePanel = document.getElementById('map-vote-panel') as HTMLDivElement;
  const mapInfoDimensions = document.getElementById('map-info-dimensions') as HTMLElement;
  const mapInfoWallDensity = document.getElementById('map-info-wall-density') as HTMLElement;
  const mapInfoEnergyCount = document.getElementById('map-info-energy-count') as HTMLElement;
  const mapVoteSection = document.getElementById('map-vote-section') as HTMLDivElement;
  const mapVoteUp = document.getElementById('map-vote-up') as HTMLButtonElement;
  const mapVoteDown = document.getElementById('map-vote-down') as HTMLButtonElement;
  const mapVoteCount = document.getElementById('map-vote-count') as HTMLSpanElement;
  const mapVoteStatus = document.getElementById('map-vote-status') as HTMLDivElement;

  function initMapVote(replay: Replay): void {
    // Always show map metadata
    const totalTiles = replay.map.rows * replay.map.cols;
    const wallDensity = totalTiles > 0 ? (replay.map.walls.length / totalTiles * 100).toFixed(1) : '0';
    mapInfoDimensions.textContent = `${replay.map.cols} x ${replay.map.rows}`;
    mapInfoWallDensity.textContent = `${wallDensity}% (${replay.map.walls.length} tiles)`;
    mapInfoEnergyCount.textContent = String(replay.map.energy_nodes.length);
    mapVotePanel.style.display = '';

    // Voting requires a map_id (available in newer replays via config)
    const mapId = replay.config.map_id;
    if (!mapId) {
      mapVoteSection.innerHTML = '<div class="map-vote-status" style="margin-top:0">Voting unavailable (no map ID)</div>';
      return;
    }

    // Enable buttons
    mapVoteUp.disabled = false;
    mapVoteDown.disabled = false;

    // Load existing votes
    fetchMapVotes(mapId).then(data => {
      updateMapVoteUI(data.net_votes, data.my_vote ?? null);
    }).catch(() => {
      mapVoteCount.textContent = '0';
    });

    // Wire up vote buttons
    mapVoteUp.onclick = () => submitVote(mapId, 1);
    mapVoteDown.onclick = () => submitVote(mapId, -1);
  }

  async function submitVote(mapId: string, vote: 1 | -1): Promise<void> {
    mapVoteUp.disabled = true;
    mapVoteDown.disabled = true;
    mapVoteStatus.textContent = 'Submitting...';

    try {
      const result = await submitMapVote(mapId, vote);
      updateMapVoteUI(result.net_votes, vote);
      mapVoteStatus.textContent = vote === 1 ? 'You upvoted this map' : 'You downvoted this map';
    } catch (err) {
      mapVoteStatus.textContent = String(err).replace(/^Error:\s*/, '');
      mapVoteUp.disabled = false;
      mapVoteDown.disabled = false;
    }
  }

  function updateMapVoteUI(netVotes: number, myVote: number | null): void {
    mapVoteCount.textContent = String(netVotes);
    mapVoteCount.className = 'map-vote-count' + (netVotes > 0 ? ' positive' : netVotes < 0 ? ' negative' : '');

    // Clear previous vote state
    mapVoteUp.classList.remove('voted-up');
    mapVoteDown.classList.remove('voted-down');

    if (myVote === 1) {
      mapVoteUp.classList.add('voted-up');
      mapVoteUp.disabled = true;
      mapVoteDown.disabled = true;
      mapVoteStatus.textContent = 'You upvoted this map';
    } else if (myVote === -1) {
      mapVoteDown.classList.add('voted-down');
      mapVoteUp.disabled = true;
      mapVoteDown.disabled = true;
      mapVoteStatus.textContent = 'You downvoted this map';
    }
  }

  function initDebugPanel(replay: Replay): void {
    const playerColors = [
      '#332288', '#88ccee', '#44aa99', '#117733', '#999933', '#ddcc77',
    ];

    debugPlayerToggles.innerHTML = '';
    replay.players.forEach((player, idx) => {
      const color = playerColors[idx] || '#888';
      const label = document.createElement('label');
      label.className = 'debug-player-toggle';
      label.innerHTML = `
        <input type="checkbox" id="debug-toggle-p${idx}" checked>
        <span class="debug-player-dot" style="background:${color}"></span>
        ${player.name}
      `;
      const checkbox = label.querySelector('input') as HTMLInputElement;
      checkbox.addEventListener('change', () => {
        viewer.setShowDebug(true);
        viewer.setDebugPlayerEnabled(idx, checkbox.checked);
        updateDebugDisplay(viewer.getDebugForCurrentTurn?.() ?? null);
      });
      debugPlayerToggles.appendChild(label);
    });

    viewer.setShowDebug(true);
  }

  // ── Annotation overlay integration (§16.8) ──────────────────────────────────────

  let annotationOverlay: AnnotationOverlay | null = null;
  let eventTimeline: EventTimeline | null = null;
  let allAnnotations: Annotation[] = [];
  let clickedGridPosition: Position | undefined;
  let canvasAnnotationHint: HTMLDivElement | null = null;

  function syncAnnotationsToViewer(): void {
    // Push annotations to the canvas renderer for marker drawing
    viewer.setAnnotations(allAnnotations);
    // Push annotations to the event timeline for badge rendering
    eventTimeline?.setAnnotations(allAnnotations);
  }

  function initAnnotations(replay: Replay): void {
    const overlayContainer = document.getElementById('annotation-overlay-container');
    const formContainer = document.getElementById('annotation-form-container');
    if (!overlayContainer || !formContainer) return;

    // Initialize EventTimeline (desktop)
    const timelineContainer = document.getElementById('event-timeline-container');
    if (timelineContainer) {
      eventTimeline = new EventTimeline(timelineContainer, {
        onTurnClick: (turn: number) => {
          viewer.setTurn(turn);
          updateUI();
          updateEventLog();
        },
      });
      // Extract events from replay turns and feed to timeline with win probability data
      const timelineTurns = replay.turns.map((t: any, i: number) => ({
        turn: i,
        events: t.events ?? [],
        energy_collected: t.energy_collected ?? [],
      }));
      eventTimeline.setEvents(timelineTurns, replay.win_prob);
      timelineContainer.style.display = '';
    }

    annotationOverlay = new AnnotationOverlay(overlayContainer, {
      onTurnClick: (turn: number) => {
        viewer.setTurn(turn);
        updateUI();
        updateEventLog();
        updateAnnotationOverlay();
      },
    });

    // Load both server and local annotations
    const local = loadLocalAnnotations(replay.match_id);
    fetchFeedback(replay.match_id).then(remote => {
      allAnnotations = [...remote, ...local];
      annotationOverlay?.loadAnnotations(replay.match_id, allAnnotations, replay.turns.length);
      syncAnnotationsToViewer();
    }).catch(() => {
      allAnnotations = local;
      annotationOverlay?.loadAnnotations(replay.match_id, allAnnotations, replay.turns.length);
      syncAnnotationsToViewer();
    });

    // Create the annotation form
    createAnnotationForm(formContainer, () => viewer.getTurn(), () => replay.match_id, () => clickedGridPosition);

    // Listen for new annotations from the form
    formContainer.addEventListener('annotation-added', ((e: CustomEvent) => {
      const ann = e.detail as Annotation;
      allAnnotations.push(ann);
      annotationOverlay?.addAnnotation(ann);
      syncAnnotationsToViewer();
      clickedGridPosition = undefined;
      if (canvasAnnotationHint) canvasAnnotationHint.classList.remove('visible');
    }) as EventListener);

    // Add canvas hint overlay
    const canvasWrapper = canvas.parentElement;
    if (canvasWrapper && !canvasWrapper.querySelector('.annotation-canvas-hint')) {
      canvasAnnotationHint = document.createElement('div');
      canvasAnnotationHint.className = 'annotation-canvas-hint';
      canvasAnnotationHint.textContent = 'Click canvas to pin annotation position';
      canvasWrapper.style.position = 'relative';
      canvasWrapper.appendChild(canvasAnnotationHint);
    }

    updateAnnotationOverlay();
  }

  function updateAnnotationOverlay(): void {
    if (annotationOverlay) {
      annotationOverlay.setCurrentTurn(viewer.getTurn());
    }
    if (eventTimeline) {
      eventTimeline.setCurrentTurn(viewer.getTurn());
    }
  }

  // Handle canvas clicks for follow player selection and annotation position
  canvas.addEventListener('click', (e: MouseEvent) => {
    if (!viewer.getReplay()) return;
    const replay = viewer.getReplay();
    if (!replay) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);
    const cellSize = viewer.getCellSize();
    const mapRows = replay.map.rows;
    const mapHeight = mapRows * cellSize;
    const overlayY = mapHeight + 4;
    const overlayPadding = 8;
    const lineHeight = 20;

    // Check if click is on score overlay (follow player selection)
    if (y >= overlayY + overlayPadding && x < replay.map.cols * cellSize) {
      const relY = y - overlayY - overlayPadding;
      const playerIdx = Math.floor(relY / lineHeight);
      if (playerIdx >= 0 && playerIdx < replay.players.length) {
        viewer.setFollowPlayer(viewer.getFollowPlayer() === playerIdx ? null : playerIdx);
        return;
      }
    }

    // Only accept clicks within the map area (not score overlay)
    const col = Math.floor(x / cellSize);
    const row = Math.floor(y / cellSize);

    if (row >= 0 && row < mapRows && col >= 0 && col < replay.map.cols) {
      clickedGridPosition = { row, col };
      if (canvasAnnotationHint) {
        canvasAnnotationHint.textContent = `Pinned: (${row}, ${col})`;
        canvasAnnotationHint.classList.add('visible');
      }
    }
  });

  function updateDebugDisplay(debug: Record<number, DebugInfo> | null): void {
    if (!debug || Object.keys(debug).length === 0) {
      debugInfoDisplay.innerHTML = '<div class="no-debug-data">No debug data for this turn</div>';
      return;
    }

    const playerColors = [
      '#332288', '#88ccee', '#44aa99', '#117733', '#999933', '#ddcc77',
    ];
    const replay = viewer.getReplay() as Replay | null;

    let html = '';
    for (const [playerId, info] of Object.entries(debug)) {
      const idx = parseInt(playerId, 10);
      if (viewer.getDebugPlayerEnabled(idx) === false) continue;
      const color = playerColors[idx] || '#888';
      const playerName = replay?.players[idx]?.name ?? `Player ${idx}`;
      const hasData = !!(info.reasoning || (info.targets && info.targets.length > 0) || (info.values && Object.keys(info.values).length > 0) || info.heatmap);
      if (!hasData) continue;

      html += `<div class="debug-player-info">
        <div class="debug-player-name" style="color:${color}">${playerName}</div>`;

      if (info.reasoning) {
        html += `<div class="debug-reasoning">${escapeHtml(info.reasoning)}</div>`;
      }

      // Display values key-value table
      if (info.values && Object.keys(info.values).length > 0) {
        html += '<div class="debug-values">';
        for (const [key, value] of Object.entries(info.values)) {
          const displayValue = typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value);
          html += `<div class="debug-value-item">
            <span class="debug-value-key">${escapeHtml(key)}:</span>
            <span class="debug-value-value">${escapeHtml(displayValue)}</span>
          </div>`;
        }
        html += '</div>';
      }

      if (info.targets && info.targets.length > 0) {
        html += '<div class="debug-targets">';
        for (const t of info.targets) {
          const pct = t.priority !== undefined ? Math.round(t.priority * 100) : 100;
          const label = t.label ? escapeHtml(t.label) : `(${t.position.row},${t.position.col})`;
          html += `<div class="debug-target-item">
            <span style="color:${t.color || color}">●</span>
            ${label}
            <span class="debug-target-priority">${pct}%</span>
          </div>`;
        }
        html += '</div>';
      }

      // Display heatmap info
      if (info.heatmap) {
        html += `<div class="debug-heatmap">
          <span class="debug-heatmap-label">Heatmap:</span>
          <span class="debug-heatmap-name">${escapeHtml(info.heatmap.name)}</span>
          <span class="debug-heatmap-size">(${info.heatmap.data.length}x${info.heatmap.data[0]?.length || 0})</span>
        </div>`;
      }

      html += '</div>';
    }

    debugInfoDisplay.innerHTML = html || '<div class="no-debug-data">No debug data for this turn</div>';
  }

  function escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function toggleDebugPanel(): void {
    debugPanelExpanded = !debugPanelExpanded;
    debugPanel.classList.toggle('expanded', debugPanelExpanded);
    debugPanelToggleBtn.setAttribute('aria-expanded', String(debugPanelExpanded));
  }

  function toggleTranscriptPanel(): void {
    transcriptPanelExpanded = !transcriptPanelExpanded;
    transcriptPanel.classList.toggle('expanded', transcriptPanelExpanded);
    transcriptPanelToggleBtn.setAttribute('aria-expanded', String(transcriptPanelExpanded));
  }

  function updateTranscript(): void {
    if (!viewer.getReplay()) {
      transcriptContent.innerHTML = '<div class="no-transcript">Load a replay to view transcript</div>';
      return;
    }

    const transcript = viewer.generateTranscript();
    const currentTurn = viewer.getTurn();

    transcriptContent.innerHTML = transcript.map((entry: { turn: number; text: string }) => {
      const isCurrent = entry.turn === currentTurn;
      return `<div class="transcript-entry${isCurrent ? ' turn-current' : ''}">
        <span class="transcript-turn-number">${entry.turn}:</span>
        ${entry.text}
      </div>`;
    }).join('');
  }

  // ── Director Mode (§16.10) ──────────────────────────────────────────────────

  function initDirector(replay: Replay): void {
    const densities = computeAllDensities(replay);
    directorSchedule = computeSpeedSchedule(densities, directorConfig.targetDuration);
    directorState = createDirectorState();

    // Apply saved duration preset selection
    updateDurationPresetUI(directorConfig.targetDuration);

    if (speedSelect.value === 'director') {
      enableDirector();
    }
  }

  function enableDirector(): void {
    directorState.enabled = true;
    directorState.pauseReason = 'none';
    viewer.setDirectorMode(true);
    directorOptions.style.display = '';
    updateDirectorSpeed();
    startDirectorTick();
  }

  function disableDirector(): void {
    directorState.enabled = false;
    viewer.setDirectorMode(false);
    directorOptions.style.display = 'none';
    stopDirectorTick();
  }

  function startDirectorTick(): void {
    stopDirectorTick();
    function tick() {
      if (!directorState.enabled) return;
      const now = performance.now();
      const turn = viewer.getTurn();
      const ms = tickDirectorSpeed(directorState, directorSchedule, turn, now);
      viewer.setDirectorSpeed(ms);
      updateDirectorStatusUI();
      directorAnimFrame = requestAnimationFrame(tick);
    }
    directorAnimFrame = requestAnimationFrame(tick);
  }

  function stopDirectorTick(): void {
    if (directorAnimFrame !== null) {
      cancelAnimationFrame(directorAnimFrame);
      directorAnimFrame = null;
    }
  }

  function updateDirectorSpeed(): void {
    if (!directorState.enabled) return;
    const now = performance.now();
    const turn = viewer.getTurn();
    const ms = tickDirectorSpeed(directorState, directorSchedule, turn, now);
    viewer.setDirectorSpeed(ms);
    updateDirectorStatusUI();
  }

  function updateDirectorStatusUI(): void {
    if (!directorState.enabled) {
      directorStatus.textContent = '';
      return;
    }
    const transitioning = directorState.easeStartTime > 0 &&
      (performance.now() - directorState.easeStartTime) < 500;
    directorStatus.textContent = formatDirectorLabel(
      directorState.currentMultiplier,
      directorState.targetMultiplier,
      transitioning,
    );
  }

  function updateDurationPresetUI(target: DurationPreset): void {
    durationPresets.querySelectorAll<HTMLElement>('.duration-btn').forEach(btn => {
      const val = parseInt(btn.dataset.duration!, 10) as DurationPreset;
      btn.classList.toggle('active', val === target);
    });
  }

  function setDurationPreset(target: DurationPreset): void {
    directorConfig.targetDuration = target;
    saveDirectorConfig(directorConfig);
    updateDurationPresetUI(target);
    // Recompute schedule with new target
    const replay = viewer.getReplay();
    if (replay) {
      const densities = computeAllDensities(replay);
      directorSchedule = computeSpeedSchedule(densities, directorConfig.targetDuration);
      directorState = createDirectorState();
      directorState.enabled = true;
    }
  }

  function initWinProb(replay: Replay): void {
    if (!replay.win_prob || replay.win_prob.length === 0) {
      winProbSection.style.display = 'none';
      return;
    }

    // Map win_prob: number[][] → WinProbPoint[] (one probs array per turn)
    const points = replay.win_prob.map((probs: number[], t: number) => ({
      turn: t,
      probs: probs.slice(),  // copy to avoid mutation
    }));

    criticalMoments = replay.critical_moments ?? [];

    // Build player colors array matching the viewer's palette
    const playerColors = replay.players.map((_: any, idx: number) => {
      const palettes = [
        '#332288', '#88ccee', '#44aa99', '#117733', '#999933', '#ddcc77',
        '#882255', '#cc6677',
      ];
      return palettes[idx] ?? '#888888';
    });

    viewer.setWinProbabilityData(points);
    viewer.setCriticalMoments(criticalMoments);
    viewer.setWinProbPlayerColors(playerColors);

    winProbSection.style.display = 'block';

    // Dynamic legend: one entry per player
    winProbLegend.innerHTML = replay.players.map((player: any, idx: number) => {
      const color = playerColors[idx];
      const dash = idx === 0 ? '&#8212;' : '--';
      return `<span style="color:${color}">${dash} ${player.name}</span>`;
    }).join(' ');

    winProbContainer.innerHTML = '';
    viewer.createWinProbSparkline(winProbContainer, 800, 70, (turn: number) => {
      viewer.setTurn(turn);
      updateUI();
      updateEventLog();
      updateCriticalMomentNav();
    });

    updateCriticalMomentNav();
  }

  function updateCriticalMomentNav(): void {
    const hasMoments = criticalMoments.length > 0;
    prevCriticalBtn.disabled = !hasMoments;
    nextCriticalBtn.disabled = !hasMoments;

    if (hasMoments) {
      const currentTurn = viewer.getTurn();
      const atMoment = criticalMoments.find((m: any) => m.turn === currentTurn);
      if (atMoment) {
        criticalMomentInfo.textContent = atMoment.description;
      } else {
        criticalMomentInfo.textContent = `${criticalMoments.length} critical moment${criticalMoments.length !== 1 ? 's' : ''}`;
      }
    } else {
      criticalMomentInfo.textContent = '—';
    }
  }

  function navigateToPrevCriticalMoment(): void {
    const currentTurn = viewer.getTurn();
    const prev = [...criticalMoments].reverse().find((m: any) => m.turn < currentTurn);
    if (prev) {
      viewer.setTurn(prev.turn);
      updateUI();
      updateEventLog();
      criticalMomentInfo.textContent = prev.description;
    }
  }

  function navigateToNextCriticalMoment(): void {
    const currentTurn = viewer.getTurn();
    const next = criticalMoments.find((m: any) => m.turn > currentTurn);
    if (next) {
      viewer.setTurn(next.turn);
      updateUI();
      updateEventLog();
      criticalMomentInfo.textContent = next.description;
    }
  }

  prevCriticalBtn.addEventListener('click', navigateToPrevCriticalMoment);
  nextCriticalBtn.addEventListener('click', navigateToNextCriticalMoment);

  function showLoadError(msg: string, url?: string): void {
    noReplayDiv.style.display = '';
    let displayMsg = msg;
    if (url) {
      displayMsg += `<br><small style="color:#9ca3af">${escapeHtml(url)}</small></small>`;
    }
    noReplayDiv.innerHTML = `<span style="color:#f87171">${displayMsg}</span>`;
  }

  loadUrlBtn.addEventListener('click', async () => {
    const url = urlInput.value.trim();
    if (!url) return;
    try {
      const replay = await fetchReplayFromUrl(url);
      loadReplay(replay);
    } catch (err) {
      const errMsg = String(err);
      if (errMsg.includes('HTTP 404')) {
        showLoadError('This replay is not available yet — it may not have been uploaded.', url);
      } else if (errMsg.includes('HTTP')) {
        showLoadError(`Could not load this replay: ${errMsg}`, url);
      } else if (errMsg.includes('JSON')) {
        showLoadError('Could not parse replay: invalid JSON format', url);
      } else {
        showLoadError(`Could not load this replay: ${escapeHtml(errMsg)}`, url);
      }
    }
  });

  playBtn.addEventListener('click', () => viewer.togglePlay());
  prevBtn.addEventListener('click', () => { viewer.setTurn(viewer.getTurn() - 1); updateUI(); updateEventLog(); });
  nextBtn.addEventListener('click', () => { viewer.setTurn(viewer.getTurn() + 1); updateUI(); updateEventLog(); });
  resetBtn.addEventListener('click', () => { viewer.pause(); viewer.setTurn(0); updateUI(); updateEventLog(); });

  turnSlider.addEventListener('input', () => {
    if (directorState.enabled) directorState.pauseReason = 'scrubbing';
    viewer.setTurn(parseInt(turnSlider.value, 10));
    updateUI();
    updateEventLog();
  });
  turnSlider.addEventListener('change', () => {
    if (directorState.enabled) directorState.pauseReason = 'none';
  });

  speedSlider.addEventListener('input', () => {
    const speed = parseInt(speedSlider.value, 10);
    viewer.setSpeed(speed);
    speedDisplay.textContent = String(speed);
    // If user manually drags speed slider, switch off Director mode
    if (directorState.enabled) {
      speedSelect.value = String(speed);
      disableDirector();
    }
  });

  speedSelect.addEventListener('change', () => {
    const val = speedSelect.value;
    if (val === 'director') {
      enableDirector();
      // Update the slider to reflect current director speed
      speedSlider.value = String(Math.round(directorState.easedMsPerTurn));
      speedDisplay.textContent = 'Director';
    } else {
      disableDirector();
      const speed = parseInt(val, 10);
      viewer.setSpeed(speed);
      speedSlider.value = String(speed);
      speedDisplay.textContent = String(speed);
    }
  });

  durationPresets.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('.duration-btn') as HTMLElement | null;
    if (!btn) return;
    const duration = parseInt(btn.dataset.duration!, 10) as DurationPreset;
    setDurationPreset(duration);
  });

  fogSelect.addEventListener('change', () => {
    const value = fogSelect.value;
    viewer.setFogOfWar(value === '' ? null : parseInt(value, 10));
  });

  viewModeSelect.addEventListener('change', () => {
    viewer.setViewMode(viewModeSelect.value as ViewMode);
  });

  cellSizeSelect.addEventListener('change', () => {
    const size = parseInt(cellSizeSelect.value, 10);
    const replay = viewer.getReplay();
    if (replay) {
      const prevTurn = viewer.getTurn();
      viewer.destroy();
      viewer = new ReplayViewerClass(canvas, { cellSize: size });
      if (minimapCanvas) viewer.setMinimapCanvas(minimapCanvas);
      loadReplay(replay);
      viewer.setTurn(prevTurn);
      updateUI();
    }
  });

  const followZoomSelect = document.getElementById('follow-zoom-select') as HTMLSelectElement;
  followZoomSelect.addEventListener('change', () => {
    viewer.setFollowZoom(parseInt(followZoomSelect.value, 10));
  });

  // Accessibility toggle handlers
  const colorBlindToggle = document.getElementById('color-blind-toggle') as HTMLInputElement;
  const shapesToggle = document.getElementById('shapes-toggle') as HTMLInputElement;
  const highContrastToggle = document.getElementById('high-contrast-toggle') as HTMLInputElement;
  const reducedMotionToggle = document.getElementById('reduced-motion-toggle') as HTMLInputElement;
  const hapticToggle = document.getElementById('haptic-toggle') as HTMLInputElement;

  function updateAccessibility(): void {
    viewer.setAccessibility({
      colorBlindSafe: colorBlindToggle.checked,
      showShapes: shapesToggle.checked,
      highContrast: highContrastToggle.checked,
      reducedMotion: reducedMotionToggle.checked,
    });
  }

  colorBlindToggle.addEventListener('change', updateAccessibility);
  shapesToggle.addEventListener('change', updateAccessibility);
  highContrastToggle.addEventListener('change', updateAccessibility);
  reducedMotionToggle.addEventListener('change', updateAccessibility);

  // Haptic feedback toggle (§16.18)
  hapticToggle.checked = isHapticEnabled();
  hapticToggle.addEventListener('change', () => {
    setHapticEnabled(hapticToggle.checked);
  });

  // Initialize accessibility from system preferences
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    reducedMotionToggle.checked = true;
    updateAccessibility();
  }

  debugPanelToggleBtn.addEventListener('click', toggleDebugPanel);
  debugPanelToggleBtn.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDebugPanel(); }
  });

  transcriptPanelToggleBtn.addEventListener('click', toggleTranscriptPanel);
  transcriptPanelToggleBtn.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleTranscriptPanel(); }
  });

  viewer.onTurnChange = () => {
    updateUI();
    updateEventLog();
    updateCriticalMomentNav();
    updateMobileUI();
    updateMobileTimeline();
    viewer.refreshWinProbSparkline();
    updateAnnotationOverlay();
    updateTranscript();

    // Haptic feedback at critical moments (§16.18)
    if (!isHapticEnabled()) return;
    const turn = viewer.getTurn();
    const replay = viewer.getReplay() as Replay | null;
    if (!replay || !replay.turns[turn]) return;

    const turnData = replay.turns[turn];
    const events = turnData.events ?? [];
    let hasCriticalEvent = false;

    // Check for combat deaths and core captures
    for (const event of events) {
      if (event.type === 'bot_died' || event.type === 'core_captured') {
        hasCriticalEvent = true;
        break;
      }
    }

    // Check for win probability shift >15%
    if (!hasCriticalEvent && replay.win_prob && turn > 0) {
      const prevProbs = replay.win_prob[turn - 1];
      const currProbs = replay.win_prob[turn];
      if (prevProbs && currProbs && prevProbs.length >= 2 && currProbs.length >= 2) {
        const delta = Math.abs(currProbs[0] - prevProbs[0]);
        if (delta > 0.15) {
          hasCriticalEvent = true;
        }
      }
    }

    if (hasCriticalEvent) {
      hapticPulse(50);
    }
  };
  viewer.onDebugChange = (debug: Record<number, DebugInfo> | null) => {
    updateDebugDisplay(debug);
  };
  viewer.onPlayStateChange = (playing: boolean) => {
    playBtn.textContent = playing ? 'Pause' : 'Play';
    mobilePlayBtn.textContent = playing ? '⏸' : '▶';
  };
  // Follow camera indicator (§16.12)
  const followIndicator = document.getElementById('follow-indicator') as HTMLDivElement;
  viewer.onFollowChange = (player: number | null) => {
    const replay = viewer.getReplay() as Replay | null;
    if (player !== null && replay && player < replay.players.length) {
      const name = replay.players[player].name;
      followIndicator.innerHTML = `Following: ${name} <span style="color:#94a3b8;margin-left:6px;cursor:pointer" title="Exit follow mode (Esc)">[×]</span>`;
      followIndicator.style.display = 'block';
      // Auto-pair fog of war with followed player
      fogSelect.value = String(player);
      viewer.setFogOfWar(player);
    } else {
      followIndicator.style.display = 'none';
      followIndicator.innerHTML = '';
      // Clear auto-paired fog of war
      fogSelect.value = '';
      viewer.setFogOfWar(null);
    }
  };
  followIndicator.addEventListener('click', () => {
    // Click the × or the whole bar to exit follow
    viewer.setFollowPlayer(null);
  });

  viewer.onCommentaryChange = (entry: { turn: number; text: string; type: string } | null) => {
    if (!entry || !commentaryEnabled) {
      commentaryText.textContent = '';
      return;
    }
    commentaryText.textContent = entry.text;
    commentaryText.className = `commentary-text type-${entry.type}`;
  };

  // ── Mobile controls ─────────────────────────────────────────────────────────
  mobilePlayBtn.addEventListener('click', () => viewer.togglePlay());
  mobilePrevBtn.addEventListener('click', () => {
    viewer.setTurn(viewer.getTurn() - 1);
    updateUI(); updateEventLog(); updateMobileUI(); updateMobileTimeline();
  });
  mobileNextBtn.addEventListener('click', () => {
    viewer.setTurn(viewer.getTurn() + 1);
    updateUI(); updateEventLog(); updateMobileUI(); updateMobileTimeline();
  });
  mobileResetBtn.addEventListener('click', () => {
    viewer.pause(); viewer.setTurn(0);
    updateUI(); updateEventLog(); updateMobileUI(); updateMobileTimeline();
  });
  mobileTurnSlider.addEventListener('input', () => {
    if (directorState.enabled) directorState.pauseReason = 'scrubbing';
    viewer.setTurn(parseInt(mobileTurnSlider.value, 10));
    updateUI(); updateEventLog(); updateMobileUI(); updateMobileTimeline();
  });
  mobileTurnSlider.addEventListener('change', () => {
    if (directorState.enabled) directorState.pauseReason = 'none';
  });
  mobileSpeedBtn.addEventListener('click', () => {
    mobileSpeedIdx = (mobileSpeedIdx + 1) % SPEED_STEPS.length;
    const speed = SPEED_STEPS[mobileSpeedIdx];
    viewer.setSpeed(speed);
    speedDisplay.textContent = String(speed);
    speedSlider.value = String(speed);
    mobileSpeedBtn.textContent = `${speed}ms`;
  });

  // Floating view mode toggle
  mobileViewModeBtn.addEventListener('click', () => {
    const current = viewer.getViewMode();
    const idx = VIEW_MODES.indexOf(current as any);
    const next = VIEW_MODES[(idx + 1) % VIEW_MODES.length];
    viewer.setViewMode(next);
    mobileViewModeBtn.textContent = VIEW_MODE_ICONS[next] ?? '👁';
  });

  // ── Canvas touch gestures ────────────────────────────────────────────────────
  // Tap = play/pause; horizontal swipe = prev/next turn; two-finger pinch = zoom

  let tapStartX = 0;
  let tapStartY = 0;
  let tapStartTime = 0;

  canvas.addEventListener('pointerdown', (e: PointerEvent) => {
    activePointers.set(e.pointerId, e);
    canvas.setPointerCapture(e.pointerId);

    if (activePointers.size === 1) {
      tapStartX = e.clientX;
      tapStartY = e.clientY;
      tapStartTime = Date.now();
    } else if (activePointers.size === 2) {
      const pts = [...activePointers.values()];
      const dx = pts[0].clientX - pts[1].clientX;
      const dy = pts[0].clientY - pts[1].clientY;
      pinchStartDist = Math.sqrt(dx * dx + dy * dy);
      pinchStartCellSize = viewer.getCellSize();
    }
  });

  canvas.addEventListener('pointermove', (e: PointerEvent) => {
    activePointers.set(e.pointerId, e);

    if (activePointers.size === 2) {
      const pts = [...activePointers.values()];
      const dx = pts[0].clientX - pts[1].clientX;
      const dy = pts[0].clientY - pts[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (pinchStartDist > 0) {
        const newSize = Math.round(pinchStartCellSize * (dist / pinchStartDist));
        viewer.setCellSize(newSize);
      }
    }
  });

  canvas.addEventListener('pointerup', (e: PointerEvent) => {
    const wasOne = activePointers.size === 1;
    const endX = e.clientX;
    const endY = e.clientY;
    activePointers.delete(e.pointerId);

    if (wasOne) {
      const dx = endX - tapStartX;
      const dy = endY - tapStartY;
      const elapsed = Date.now() - tapStartTime;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (elapsed < 300 && dist < 12) {
        // Tap: play/pause
        if (viewer.getReplay()) viewer.togglePlay();
      } else if (elapsed < 500 && Math.abs(dx) > 40 && Math.abs(dy) < 50) {
        // Horizontal swipe: scrub turn
        if (!viewer.getReplay()) return;
        if (dx < 0) {
          viewer.setTurn(viewer.getTurn() + 1);
        } else {
          viewer.setTurn(viewer.getTurn() - 1);
        }
        updateUI(); updateEventLog(); updateMobileUI(); updateMobileTimeline();
      }
    }

    if (activePointers.size < 2) {
      pinchStartDist = 0;
    }
  });

  canvas.addEventListener('pointercancel', (e: PointerEvent) => {
    activePointers.delete(e.pointerId);
    if (activePointers.size < 2) pinchStartDist = 0;
  });

  // Commentary toggle
  commentaryToggle.addEventListener('click', () => {
    commentaryEnabled = !commentaryEnabled;
    viewer.setCommentaryEnabled(commentaryEnabled);
    commentaryToggle.classList.toggle('active', commentaryEnabled);
    if (!commentaryEnabled) {
      commentaryText.textContent = '';
    }
  });

  // ── Keyboard Shortcuts Overlay (§15.3) ────────────────────────────────────────────

  function showKeyboardShortcutsOverlay(): void {
    // Remove existing overlay if present
    const existing = document.getElementById('keyboard-shortcuts-overlay');
    if (existing) {
      existing.remove();
      return;
    }

    const overlay = document.createElement('div');
    overlay.id = 'keyboard-shortcuts-overlay';
    overlay.className = 'keyboard-shortcuts-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'shortcuts-title');
    overlay.innerHTML = `
      <div class="shortcuts-modal">
        <div class="shortcuts-header">
          <h2 id="shortcuts-title">Keyboard Shortcuts</h2>
          <button class="shortcuts-close" aria-label="Close shortcuts" title="Press Esc or ? to close">&times;</button>
        </div>
        <div class="shortcuts-content">
          <div class="shortcuts-section">
            <h3>Playback Controls</h3>
            <div class="shortcut-row"><kbd>Space</kbd> <span>Play / Pause</span></div>
            <div class="shortcut-row"><kbd>←</kbd> / <kbd>→</kbd> <span>Step one turn</span></div>
            <div class="shortcut-row"><kbd>Shift</kbd>+<kbd>←</kbd> / <kbd>→</kbd> <span>Jump 10 turns</span></div>
            <div class="shortcut-row"><kbd>Home</kbd> / <kbd>End</kbd> <span>First / Last turn</span></div>
            <div class="shortcut-row"><kbd>[</kbd> / <kbd>]</kbd> <span>Prev / Next critical moment</span></div>
            <div class="shortcut-row"><kbd>1</kbd>–<kbd>6</kbd> <span>Follow player (0 to exit)</span></div>
          </div>
          <div class="shortcuts-section">
            <h3>View Options</h3>
            <div class="shortcut-row"><kbd>V</kbd> <span>Cycle view mode</span></div>
            <div class="shortcut-row"><kbd>F</kbd> <span>Theater mode</span></div>
            <div class="shortcut-row"><kbd>E</kbd> <span>Toggle event timeline</span></div>
            <div class="shortcut-row"><kbd>T</kbd> <span>Toggle transcript panel</span></div>
            <div class="shortcut-row"><kbd>C</kbd> <span>Toggle commentary</span></div>
          </div>
          <div class="shortcuts-section">
            <h3>Accessibility</h3>
            <div class="shortcut-row"><kbd>?</kbd> / <kbd>/</kbd> <span>Show this help</span></div>
            <div class="shortcut-row"><kbd>Esc</kbd> <span>Exit follow mode / Close modal</span></div>
          </div>
        </div>
        <div class="shortcuts-footer">
          <p>Use checkboxes in the sidebar for: Color-blind palette, High contrast, Reduced motion</p>
        </div>
      </div>
    `;

    // Inject styles for the overlay
    const style = document.createElement('style');
    style.textContent = `
      .keyboard-shortcuts-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.85);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        animation: shortcuts-fade-in 0.2s ease;
      }
      @keyframes shortcuts-fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      .shortcuts-modal {
        background: var(--bg-secondary, #1e293b);
        border-radius: 12px;
        max-width: 500px;
        width: 90vw;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        border: 1px solid var(--border, #334155);
      }
      .shortcuts-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid var(--border, #334155);
      }
      .shortcuts-header h2 {
        margin: 0;
        font-size: 1.25rem;
        color: var(--text-primary, #f8fafc);
      }
      .shortcuts-close {
        background: none;
        border: none;
        font-size: 1.5rem;
        color: var(--text-muted, #94a3b8);
        cursor: pointer;
        padding: 4px 12px;
        line-height: 1;
        min-width: 44px;
        min-height: 44px;
        transition: color 0.15s;
      }
      .shortcuts-close:hover {
        color: var(--text-primary, #f8fafc);
      }
      .shortcuts-content {
        padding: 20px;
      }
      .shortcuts-section {
        margin-bottom: 20px;
      }
      .shortcuts-section:last-child {
        margin-bottom: 0;
      }
      .shortcuts-section h3 {
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--text-muted, #94a3b8);
        margin: 0 0 12px 0;
        border-bottom: 1px solid var(--border, #334155);
        padding-bottom: 8px;
      }
      .shortcut-row {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 10px;
        font-size: 0.9rem;
      }
      .shortcut-row:last-child {
        margin-bottom: 0;
      }
      .shortcut-row kbd {
        background: var(--bg-tertiary, #334155);
        border: 1px solid var(--border, #475569);
        border-radius: 4px;
        padding: 4px 10px;
        font-family: monospace;
        font-size: 0.85rem;
        color: var(--text-primary, #f8fafc);
        min-width: 32px;
        text-align: center;
        box-shadow: 0 2px 0 var(--border, #475569);
      }
      .shortcut-row span {
        color: var(--text-secondary, #e2e8f0);
      }
      .shortcuts-footer {
        padding: 15px 20px;
        border-top: 1px solid var(--border, #334155);
        background: var(--bg-tertiary, #334155);
        border-radius: 0 0 12px 12px;
      }
      .shortcuts-footer p {
        margin: 0;
        font-size: 0.8rem;
        color: var(--text-muted, #94a3b8);
      }
      @media (prefers-reduced-motion: reduce) {
        .keyboard-shortcuts-overlay {
          animation: none;
        }
      }
    `;

    // Close on click outside or Escape
    const closeModal = () => {
      overlay.remove();
    };

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeModal();
      }
    });

    const closeBtn = overlay.querySelector('.shortcuts-close') as HTMLButtonElement;
    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal);
    }

    // Close on Escape key
    const escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
        document.removeEventListener('keydown', escapeHandler);
      }
    };
    document.addEventListener('keydown', escapeHandler);

    document.body.appendChild(overlay);
    overlay.appendChild(style);

    // Focus the close button for accessibility
    closeBtn?.focus();
  }

  document.addEventListener('keydown', (e) => {
    if (!viewer.getReplay()) return;
    switch (e.code) {
      case 'Space':
        e.preventDefault();
        viewer.togglePlay();
        break;
      case 'ArrowLeft':
        if (e.shiftKey) {
          // Shift+Left: Jump 10 turns back
          e.preventDefault();
          viewer.setTurn(Math.max(0, viewer.getTurn() - 10));
        } else {
          e.preventDefault();
          viewer.setTurn(viewer.getTurn() - 1);
        }
        updateUI();
        updateEventLog();
        break;
      case 'ArrowRight':
        if (e.shiftKey) {
          // Shift+Right: Jump 10 turns forward
          e.preventDefault();
          viewer.setTurn(Math.min(viewer.getTotalTurns() - 1, viewer.getTurn() + 10));
        } else {
          e.preventDefault();
          viewer.setTurn(viewer.getTurn() + 1);
        }
        updateUI();
        updateEventLog();
        break;
      case 'Home':
        e.preventDefault();
        viewer.setTurn(0);
        updateUI();
        updateEventLog();
        break;
      case 'End':
        e.preventDefault();
        viewer.setTurn(viewer.getTotalTurns() - 1);
        updateUI();
        updateEventLog();
        break;
      case 'BracketLeft':
        e.preventDefault();
        navigateToPrevCriticalMoment();
        break;
      case 'BracketRight':
        e.preventDefault();
        navigateToNextCriticalMoment();
        break;
      case 'KeyV':
        // Cycle view mode (dots → territory → influence → standard)
        e.preventDefault();
        const currentViewMode = viewer.getViewMode();
        const viewModes: Array<'standard' | 'dots' | 'voronoi' | 'influence'> = ['dots', 'voronoi', 'influence', 'standard'];
        const idx = viewModes.indexOf(currentViewMode);
        const nextViewMode = viewModes[(idx + 1) % viewModes.length];
        viewer.setViewMode(nextViewMode);
        viewModeSelect.value = nextViewMode;
        break;
      case 'KeyF':
        if (!e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          theater.toggle();
        }
        break;
      case 'KeyT':
        e.preventDefault();
        toggleTranscriptPanel();
        break;
      case 'KeyE':
        // Toggle event timeline visibility
        e.preventDefault();
        const timelineContainer = document.getElementById('event-timeline-container');
        if (timelineContainer) {
          const isHidden = timelineContainer.style.display === 'none';
          timelineContainer.style.display = isHidden ? '' : 'none';
        }
        break;
      case 'KeyC':
        // Toggle commentary subtitles
        e.preventDefault();
        commentaryEnabled = !commentaryEnabled;
        viewer.setCommentaryEnabled(commentaryEnabled);
        commentaryToggle.classList.toggle('active', commentaryEnabled);
        if (!commentaryEnabled) {
          commentaryText.textContent = '';
        } else {
          const entry = viewer.getCommentaryForTurn(viewer.getTurn());
          if (entry) {
            commentaryText.textContent = entry.text;
            commentaryText.className = `commentary-text type-${entry.type}`;
          }
        }
        break;
      case 'Slash':
        // Show keyboard shortcuts overlay (? key without shift, or / key)
        if (e.shiftKey || e.key === '?') {
          e.preventDefault();
          showKeyboardShortcutsOverlay();
        }
        break;
      case 'Digit0':
      case 'Escape':
        if (theater.isActive()) break; // let theater handle its own escape
        e.preventDefault();
        viewer.setFollowPlayer(null);
        break;
      case 'Digit1': case 'Digit2': case 'Digit3':
      case 'Digit4': case 'Digit5': case 'Digit6':
        e.preventDefault();
        const followIdx = parseInt(e.code.replace('Digit', ''), 10) - 1;
        const replay = viewer.getReplay();
        if (replay && followIdx < replay.players.length) {
          viewer.setFollowPlayer(viewer.getFollowPlayer() === followIdx ? null : followIdx);
        }
        break;
    }
  });

  // Load from URL param if provided
  if (initialUrl) {
    urlInput.value = initialUrl;
    loadUrlBtn.click();
  }
}
